import { useState, useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
  Button,
  Tooltip,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@coreai/fluentui-react';
import { AddRegular, DeleteRegular, ArrowMaximizeRegular } from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import { ExpandableEditorDialog } from './ExpandableEditorDialog';
import { useRestoreFocusOnDialogClose } from './useRestoreFocusOnDialogClose';
import { MULTILINE_TEXT_MAX_HEIGHT } from './constants';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

/**
 * See `./constants.ts` for the sourcing rationale on
 * `MULTILINE_TEXT_MAX_HEIGHT`. Same design-system default applies to
 * both `TextAreaField` and this System-message input.
 */

// =============================================================================
// TYPES
// =============================================================================

interface AgentMessage {
  // Backend only accepts the capitalized roles 'System' and 'User'.
  role: 'System' | 'User';
  content: string;
}

export interface AgentInstructionFieldProps {
  name: string;
  label: string;
  /** Value can be JSON string, ValueSegment[], or raw messages array from workflow */
  value: string | ValueSegment[] | unknown;
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Hide the system instructions section (for hosted agents) */
  hideSystemMessage?: boolean | undefined;
  /** Hide the user instructions section (only show system instructions) */
  hideUserInstructions?: boolean | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale) */
  isStale?: boolean | undefined;
}

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  userInstructionItem: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-start',
  },
  userInstructionInput: {
    flex: 1,
  },
  deleteButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  userInstructionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  warningBar: {
    marginTop: tokens.spacingVerticalS,
  },
  // Wrapper for System-message input + inline expand button. Kept as its
  // own relative-positioned box so the expand button anchors correctly.
  systemInputWrapper: {
    position: 'relative',
  },
  // When the expand button is present, reserve inline-end space on the
  // editor so long first-line text and the internal scrollbar don't run
  // under the 24px glyph.
  systemInputWrapperWithExpand: {
    position: 'relative',
    '& [role="textbox"]': {
      paddingInlineEnd: tokens.spacingHorizontalXXL,
    },
  },
  expandButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    insetInlineEnd: tokens.spacingHorizontalXS,
    zIndex: 1,
    minWidth: 'auto',
  },
});

// =============================================================================
// PARSING UTILITIES
// =============================================================================

/**
 * Parse the messages array from the input value
 * Format: [{ role: "system", content: "..." }, { role: "user", content: "..." }, ...]
 * Handles multiple input types: JSON string, ValueSegment[], or raw array object
 */
function parseAgentInstructions(value: string | ValueSegment[] | unknown): {
  systemMessage: string;
  userMessages: string[];
  parseError: string | null;
} {
  try {
    let messages: AgentMessage[];

    // Handle different input types
    if (!value) {
      return { systemMessage: '', userMessages: [], parseError: null };
    }

    // Case 1: Already a parsed array of messages (raw object from workflow)
    if (Array.isArray(value) && !isValueSegmentArray(value)) {
      const firstItem = value[0];
      if (firstItem && typeof firstItem === 'object' && 'role' in firstItem) {
        messages = value as AgentMessage[];
      } else {
        return { systemMessage: '', userMessages: [], parseError: null };
      }
    }
    // Case 2: ValueSegment array - convert to string first
    else if (isValueSegmentArray(value)) {
      const jsonString = segmentsToString(value);
      if (!jsonString.trim()) {
        return { systemMessage: '', userMessages: [], parseError: null };
      }
      const parsed = JSON.parse(jsonString);
      if (!Array.isArray(parsed)) {
        return {
          systemMessage: '',
          userMessages: [],
          parseError: 'Expected an array of messages',
        };
      }
      messages = parsed as AgentMessage[];
    }
    // Case 3: JSON string
    else if (typeof value === 'string') {
      if (!value.trim()) {
        return { systemMessage: '', userMessages: [], parseError: null };
      }
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        return {
          systemMessage: '',
          userMessages: [],
          parseError: 'Expected an array of messages',
        };
      }
      messages = parsed as AgentMessage[];
    }
    // Case 4: Unknown type
    else {
      return { systemMessage: '', userMessages: [], parseError: null };
    }

    // Extract system and user messages. Accept both 'System'/'User' (the
    // canonical form we serialize) and 'system'/'user' (lower-case form the
    // backend deserializes and that may appear in hand-authored JSON or
    // existing workflows) so the editor stays populated on a round-trip.
    let systemMessage = '';
    const userMessages: string[] = [];

    for (const msg of messages) {
      const role = typeof msg.role === 'string' ? msg.role.toLowerCase() : '';
      if (role === 'system' && !systemMessage) {
        systemMessage = msg.content || '';
      } else if (role === 'user') {
        userMessages.push(msg.content || '');
      }
    }

    return { systemMessage, userMessages, parseError: null };
  } catch (e) {
    return {
      systemMessage: '',
      userMessages: [],
      parseError: `Error parsing agent instructions: ${e instanceof Error ? e.message : 'Unknown error'}`,
    };
  }
}

/**
 * Serialize the system and user messages back to the messages array format
 */
function serializeAgentInstructions(
  systemMessage: string,
  userMessages: string[]
): ValueSegment[] {
  const messages: AgentMessage[] = [];

  // Always include system message (even if empty).
  // Backend only accepts the capitalized roles 'System' and 'User'.
  messages.push({
    role: 'System',
    content: systemMessage,
  });

  // Add user messages (filter out empty ones)
  for (const userMsg of userMessages) {
    if (userMsg.trim()) {
      messages.push({
        role: 'User',
        content: userMsg,
      });
    }
  }

  const jsonString = JSON.stringify(messages, null, 2);

  return [
    {
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: jsonString,
    },
  ];
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * AgentInstructionField - Editor for agent instructions (system + user messages)
 *
 * The messages are stored as a JSON array:
 * [
 *   { "role": "System", "content": "You are a helpful assistant" },
 *   { "role": "User", "content": "User prompt 1" },
 *   { "role": "User", "content": "User prompt 2" }
 * ]
 */
export const AgentInstructionField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  hideSystemMessage = false,
  hideUserInstructions = false,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData: _hasRunData,
  isStale: _isStale,
}: AgentInstructionFieldProps) => {
  const styles = useStyles();

  // Parse the initial value
  const { systemMessage: initialSystem, userMessages: initialUser, parseError } = useMemo(
    () => parseAgentInstructions(value),
    [value]
  );

  // Local state for editing
  const [systemMessage, setSystemMessage] = useState(initialSystem);
  const [userMessages, setUserMessages] = useState<string[]>(
    initialUser.length > 0 ? initialUser : ['']
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(parseError);

  // Expand-to-dialog state for the System-message input. User instruction
  // rows are single-line (`multiline={false}`) and don't need it.
  const [isSystemDialogOpen, setIsSystemDialogOpen] = useState(false);
  const canExpandSystem = !disabled;
  const openSystemDialog = useCallback(() => setIsSystemDialogOpen(true), []);
  const closeSystemDialog = useCallback(() => setIsSystemDialogOpen(false), []);
  // Restore focus to the expand button on close — it unmounts while the dialog
  // is open, so the dialog itself can't do this (see the hook).
  const expandButtonRef =
    useRestoreFocusOnDialogClose<HTMLButtonElement>(isSystemDialogOpen);

  // Convert system message string to segments for TokenizedInput
  const systemSegments: ValueSegment[] = useMemo(
    () =>
      systemMessage
        ? [
            {
              id: generateSegmentId(),
              type: ValueSegmentType.LITERAL,
              value: systemMessage,
            },
          ]
        : [],
    [systemMessage]
  );

  // Serialize and notify parent of changes
  const notifyChange = useCallback(
    (newSystem: string, newUserMessages: string[]) => {
      const serialized = serializeAgentInstructions(newSystem, newUserMessages);
      onChange(serialized);
      setErrorMessage(null);
    },
    [onChange]
  );

  // Handle system message change
  const handleSystemChange = useCallback(
    (segments: ValueSegment[]) => {
      const newSystem = segmentsToString(segments);
      setSystemMessage(newSystem);
      notifyChange(newSystem, userMessages);
    },
    [userMessages, notifyChange]
  );

  // Handle user message change at index
  const handleUserMessageChange = useCallback(
    (index: number, segments: ValueSegment[]) => {
      const newValue = segmentsToString(segments);
      const newUserMessages = [...userMessages];
      newUserMessages[index] = newValue;
      setUserMessages(newUserMessages);
      notifyChange(systemMessage, newUserMessages);
    },
    [userMessages, systemMessage, notifyChange]
  );

  // Add a new user message
  const handleAddUserMessage = useCallback(() => {
    const newUserMessages = [...userMessages, ''];
    setUserMessages(newUserMessages);
    notifyChange(systemMessage, newUserMessages);
  }, [userMessages, systemMessage, notifyChange]);

  // Remove a user message at index
  const handleRemoveUserMessage = useCallback(
    (index: number) => {
      const newUserMessages = userMessages.filter((_, i) => i !== index);
      // Keep at least one empty item for UX
      if (newUserMessages.length === 0) {
        newUserMessages.push('');
      }
      setUserMessages(newUserMessages);
      notifyChange(systemMessage, newUserMessages);
    },
    [userMessages, systemMessage, notifyChange]
  );

  return (
    <div className={styles.container}>
      {/* System Instructions */}
      {!hideSystemMessage && (
      <div className={styles.section}>
        <div className={styles.labelRow}>
          <Label className={styles.label} htmlFor={`${name}-system`}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </Label>
        </div>

        {description && (
          <Text className={styles.description}>{description}</Text>
        )}

        <div
          className={
            canExpandSystem
              ? styles.systemInputWrapperWithExpand
              : styles.systemInputWrapper
          }
        >
          {/* Panel editor is unmounted while the dialog is open. This
              avoids two live-synced TokenizedInputs on the same value
              (which corrupts the panel's undo stack on every dialog
              keystroke). */}
          {!isSystemDialogOpen && (
            <TokenizedInput
              name={`${name}-system`}
              ariaLabel={label}
              value={systemSegments}
              onChange={handleSystemChange}
              placeholder={placeholder || 'Give the agent context for its role in the workflow'}
              multiline={true}
              maxHeight={MULTILINE_TEXT_MAX_HEIGHT}
              disabled={disabled}
              readonly={false}
              showAgentParameterButton={showAgentParameterButton}
              agentParameterSchema={agentParameterSchema}
              actionOutputs={actionOutputs}
            />
          )}

          {/* Expand-to-dialog for the System message, mirroring TextAreaField. */}
          {canExpandSystem && !isSystemDialogOpen && (
            <Tooltip content={`Open ${label} in editor`} relationship="label">
              <Button
                ref={expandButtonRef as unknown as React.Ref<never>}
                className={styles.expandButton}
                appearance="subtle"
                size="small"
                icon={<ArrowMaximizeRegular />}
                onClick={openSystemDialog}
                aria-label={`Open ${label} in editor`}
              />
            </Tooltip>
          )}
        </div>
      </div>
      )}

      {/* User Instructions */}
      {!hideUserInstructions && (
        <div className={styles.section}>
          <Label className={styles.label}>User instructions</Label>
          <Text className={styles.description}>
            Add optional prompts or questions for the agent. For better results, focus each item on a single specific prompt or question.
          </Text>

          <div className={styles.userInstructionsList}>
            {userMessages.map((msg, index) => (
              <div key={index} className={styles.userInstructionItem}>
                <div className={styles.userInstructionInput}>
                  <TokenizedInput
                    name={`${name}-user-${index}`}
                    value={
                      msg
                        ? [
                            {
                              id: generateSegmentId(),
                              type: ValueSegmentType.LITERAL,
                              value: msg,
                            },
                          ]
                        : []
                    }
                    onChange={(segments) => handleUserMessageChange(index, segments)}
                    placeholder="Enter a user prompt or question"
                    multiline={false}
                    disabled={disabled}
                    readonly={false}
                    showAgentParameterButton={showAgentParameterButton}
                    agentParameterSchema={agentParameterSchema}
                    actionOutputs={actionOutputs}
                  />
                </div>
                <Button
                  className={styles.deleteButton}
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => handleRemoveUserMessage(index)}
                  disabled={disabled ?? false}
                  aria-label={`Remove user instruction ${index + 1}`}
                />
              </div>
            ))}
          </div>

          <Button
            className={styles.addButton}
            appearance="subtle"
            icon={<AddRegular />}
            onClick={handleAddUserMessage}
            disabled={disabled ?? false}
          >
            Add user instruction
          </Button>
        </div>
      )}

      {/* Error display */}
      {(error || errorMessage) && (
        <MessageBar intent="warning" className={styles.warningBar}>
          <MessageBarBody>
            <MessageBarTitle>{error || errorMessage}</MessageBarTitle>
            {errorMessage && 'This could mean the instruction schema is set up incorrectly.'}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Full-height dialog for the System message. Only mounted when
          open — panel editor above is unmounted for the duration to
          keep undo/state single-owner. */}
      {!hideSystemMessage && canExpandSystem && isSystemDialogOpen && (
        <ExpandableEditorDialog
          open={isSystemDialogOpen}
          onClose={closeSystemDialog}
          label={label}
          value={systemSegments}
          onChange={handleSystemChange}
          placeholder={placeholder || 'Give the agent context for its role in the workflow'}
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      )}
    </div>
  );
};

AgentInstructionField.displayName = 'AgentInstructionField';
